import { api } from "@/Shared/Lib/Logic";
import { ChallengeModel } from "@/Domains";
import { ApiResponse} from "@/OneLiners";
import { UserSessionModel } from "./UserSessionModel";
import { PasswordFormValues } from "@/Domains/User/Logic/UserFormValidation";

/**
 * =====================================================
 * Class: AuthService
 * =====================================================
 *
 * Description:
 * Provides all authentication-related API calls for the application.
 * Handles login, logout, session validation, password management,
 * and visitor enigma challenges.
 *
 * Responsibilities:
 * - Perform user login and logout
 * - Retrieve and validate current user session
 * - Handle password updates and resets
 * - Manage visitor session creation and enigma challenge validation
 *
 * Notes:
 * - All methods return ApiResponse<T> for standardized API handling
 * - Uses the shared `api` instance for HTTP requests
 *
 * Author: Samano CASTRE
 * Date: February 1, 2026
 */
export class AuthService {

    /** =====================================================
     * Log In
     * =====================================================
     *
     * Sends user credentials to backend and retrieves a session.
     *
     * @param values object containing identifier and password
     * @returns ApiResponsecontaining UserSessionModel
     */
    static logIn = async (values: { identifier: string, password: string }): Promise<ApiResponse<UserSessionModel>> => {
        const { data } = await api.post<ApiResponse<UserSessionModel>>(`/auth/login`, {
            identifier: values.identifier,
            password: values.password
        });
        return data;
    };

    /** =====================================================
     * Get Current Session
     * =====================================================
     *
     * Retrieves the current user session from the backend.
     *
     * @returns ApiResponsecontaining UserSessionModel
     */
    static getCurrentSession = async (): Promise<ApiResponse<UserSessionModel>> => {
        const { data } = await api.get<ApiResponse<UserSessionModel>>(`/user-sessions/me`);
        return data;
    };


    /** =====================================================
    * Check Session
    * =====================================================
    *
    * Checks if the current session is still valid.
    *
    * @returns ApiResponsecontaining { authenticated: boolean }
    */
    static checkSession = async (): Promise<ApiResponse<{ authenticated: boolean }>> => {
        const { data } = await api.get<ApiResponse<{ authenticated: boolean }>>(`/auth/check`);
        return data;
    };

    /** =====================================================
     * Log Out
     * =====================================================
     *
     * Logs out the current user.
     *
     * @returns ApiResponse<void>
     */
    static logOut = async (): Promise<ApiResponse<void>> => {
        const { data } = await api.post<ApiResponse<void>>(`/auth/logout`);
        return data;
    };


    /** =====================================================
    * Update Password
    * =====================================================
    *
    * Updates the current user's password.
    *
    * @param values PasswordFormValues containing old/new password
    * @returns ApiResponse<void>
    */
    static updatePassword = async (values: PasswordFormValues): Promise<ApiResponse<void>> => {
        const { data } = await api.post<ApiResponse<void>>(`/auth/password`, values);
        return data;
    };


    /** =====================================================
     * Reset Password
     * =====================================================
     *
     * Resets a specific user's password (admin operation).
     *
     * @param userId ID of the user to reset
     * @returns ApiResponse<void>
     */
    static resetPassword = async (userId: number): Promise<ApiResponse<void>> => {
        const { data } = await api.post<ApiResponse<void>>(`/auth/user/password`, { userId });
        return data;
    };


    /** =====================================================
     * Get Visitor Enigma
     * =====================================================
     *
     * Retrieves an enigma challenge for a visitor by ID.
     *
     * @param visitorId unique visitor identifier
     * @returns ApiResponsecontaining ChallengeModel
     */
    static getVisitorEnigma = async (visitorId: string): Promise<ApiResponse<ChallengeModel>> => {
        const { data } = await api.get<ApiResponse<ChallengeModel>>(`/challenges/${visitorId}/enigma`);
        return data;
    }


    /** =====================================================
     * Validate Enigma
     * =====================================================
     *
     * Submits visitor's answer to enigma challenge for validation.
     *
     * @param id Enigma challenge ID
     * @param userDigits digits provided by visitor
     * @returns ApiResponsecontaining updated ChallengeModel
     */
    static validateEnigma = async (id: number, userDigits: string): Promise<ApiResponse<ChallengeModel>> => {
        const { data } = await api.patch<ApiResponse<ChallengeModel>>(`/challenges/${id}`, { userDigits });
        return data;
    };
}